import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../../shared/utils/pdfTurkishFont';

/** Modern color palette */
const COLORS = {
  headerBg: 'FF2D3748',
  headerFg: 'FFFFFFFF',
  lineAltBg: 'FFF7FAFC',
  lineFg: 'FF2D3748',
  border: 'FFCBD5E0'
};

/** PDF colors */
const PDF_COLORS = {
  headerBg: [45, 55, 72],
  headerFg: [255, 255, 255],
  lineAltBg: [247, 250, 252],
  lineFg: [45, 55, 72],
  lineBorder: [203, 213, 224]
};

const toNum = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

/**
 * Export Kebir Mizan to Excel (.xlsx) with modern formatting.
 * @param {Array} lines - Kebir mizan lines
 * @param {string} filename - Output filename (without extension)
 */
export async function exportToExcel(lines, filename = 'kebir_mizan') {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Kebir Mizan', {
    views: [{ rightToLeft: false }],
    properties: { defaultRowHeight: 20 }
  });

  ws.columns = [
    { width: 12 },
    { width: 45 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 10 }
  ];

  const applyCellStyle = (cell, font, fill, alignment) => {
    if (font) cell.font = font;
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    if (alignment) cell.alignment = alignment;
    cell.border = {
      top: { style: 'thin', color: { argb: COLORS.border } },
      left: { style: 'thin', color: { argb: COLORS.border } },
      bottom: { style: 'thin', color: { argb: COLORS.border } },
      right: { style: 'thin', color: { argb: COLORS.border } }
    };
  };

  const headers = ['Kebir Kod', 'Kebir Ad', 'Borç', 'Alacak', 'Bakiye', 'Satır'];
  const headerRow = ws.addRow(headers);
  headerRow.height = 28;
  headers.forEach((_, i) => {
    const cell = headerRow.getCell(i + 1);
    applyCellStyle(
      cell,
      { bold: true, size: 11, color: { argb: COLORS.headerFg } },
      COLORS.headerBg,
      i >= 2 ? { horizontal: 'right', vertical: 'middle' } : { horizontal: i === 1 ? 'left' : 'center', vertical: 'middle' }
    );
  });

  lines.forEach((line, idx) => {
    const row = ws.addRow([
      line.kebirCode,
      line.kebirName,
      toNum(line.debitTotal),
      toNum(line.creditTotal),
      toNum(line.balance),
      line.lineCount ?? 0
    ]);
    const isAlt = idx % 2 === 1;
    const font = { size: 9, color: { argb: COLORS.lineFg } };
    const fill = isAlt ? COLORS.lineAltBg : null;
    row.eachCell((cell, colNumber) => {
      if (colNumber >= 3) cell.numFmt = '#,##0.00';
      if (colNumber >= 2) cell.alignment = { horizontal: colNumber === 2 ? 'left' : 'right', vertical: 'middle' };
      else cell.alignment = { vertical: 'middle' };
      applyCellStyle(cell, font, fill, null);
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export Kebir Mizan to PDF with modern styling.
 * @param {Array} lines - Kebir mizan lines
 * @param {string} filename - Output filename (without extension)
 * @param {string} [headerText] - Optional header
 */
export async function exportToPdf(lines, filename = 'kebir_mizan', headerText = '') {
  const formatAmount = (v) => {
    const n = toNum(v);
    return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' TL';
  };

  const body = lines.map((line, idx) => [
    line.kebirCode,
    line.kebirName,
    formatAmount(line.debitTotal),
    formatAmount(line.creditTotal),
    formatAmount(line.balance),
    String(line.lineCount ?? 0)
  ]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  const margin = 14;

  if (headerText) {
    doc.setFontSize(10);
    doc.setTextColor(45, 55, 72);
    doc.text(headerText, margin, 12);
  }

  const colWidths = { 0: 25, 1: 70, 2: 35, 3: 35, 4: 35, 5: 20 };
  const haligns = { 0: 'left', 1: 'left', 2: 'right', 3: 'right', 4: 'right', 5: 'right' };

  let lineIndex = 0;
  autoTable(doc, {
    head: [['Kebir Kod', 'Kebir Ad', 'Borç', 'Alacak', 'Bakiye', 'Satır']],
    body,
    startY: headerText ? 18 : 14,
    margin: { left: margin, right: margin },
    styles: { fontSize: 7, font: TURKISH_FONT, textColor: PDF_COLORS.lineFg, lineWidth: 0.1, lineColor: PDF_COLORS.lineBorder },
    headStyles: {
      font: TURKISH_FONT,
      fillColor: PDF_COLORS.headerBg,
      textColor: PDF_COLORS.headerFg,
      fontStyle: 'bold'
    },
    columnStyles: Object.fromEntries(
      [0, 1, 2, 3, 4, 5].map((i) => [i, { cellWidth: colWidths[i], halign: haligns[i] }])
    ),
    theme: 'plain',
    didParseCell: (data) => {
      if (data.section === 'head') {
        data.cell.styles.fillColor = PDF_COLORS.headerBg;
        data.cell.styles.textColor = PDF_COLORS.headerFg;
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.halign = haligns[data.column.index];
      }
      if (data.section === 'body') {
        const isAlt = lineIndex++ % 2 === 1;
        if (isAlt) data.cell.styles.fillColor = PDF_COLORS.lineAltBg;
        data.cell.styles.textColor = PDF_COLORS.lineFg;
      }
    }
  });

  doc.save(`${filename}.pdf`);
}
